// TES-1061 (TES-1069 point 1) — the treatment decision's journal note in the
// decision's Webdoc step. Pure, so `node --test` can run it
// (see test/decision-note.test.ts).
//
// The decision is journaled in the note the server names: the care meeting's
// ("vårdmötes- och beslutsanteckning") or, with a completion, the round's
// Kompletteringsanteckning. The step shows that note with the decision on
// screen, compiled exactly as the server compiles it; "Kopiera" stores it as
// a version, and the journal tick covers that version only while the
// decision and the note it is written into are the ones copied (the TES-1063
// pattern).
import type {
  CompletionSelection,
  TreatmentDecisionNoteDraft,
  TreatmentDecisionNoteTarget,
  TreatmentDecisionOutcomeChoice,
} from "@care-by-ella/contracts";

/**
 * The decision's fields that reach the note, as the publish will send them
 * (trimmed, empty left out); null while the outcome cannot be journaled
 * yet (none chosen, or a komplettering with nothing picked).
 */
export function decisionNoteDraft(input: {
  outcome: TreatmentDecisionOutcomeChoice | null;
  completion: CompletionSelection | null;
  completionReason: string;
  declineReason: string;
  carePath: string;
  // TES-1185 — the prescription status chosen with an approval.
  prescription?: TreatmentDecisionNoteDraft["prescription"] | null;
  // TES-1185 — "Planen kvarstår" / "Ändra planerad läkemedelsbehandling",
  // and a changed plan's new order as prose (plannedOrderProse).
  plannedTreatment?: TreatmentDecisionNoteDraft["plannedTreatment"] | null;
  plannedOrderText?: string;
}): TreatmentDecisionNoteDraft | null {
  switch (input.outcome) {
    case null:
      return null;
    case "approve":
      return {
        outcome: "approve",
        ...(input.prescription ? { prescription: input.prescription } : {}),
        ...(input.plannedTreatment
          ? { plannedTreatment: input.plannedTreatment }
          : {}),
        ...(input.plannedTreatment?.choice === "changed" &&
        input.plannedOrderText
          ? { plannedOrderText: input.plannedOrderText }
          : {}),
      };
    case "completion": {
      if (!input.completion || input.completion.items.length === 0) return null;
      const reason = input.completionReason.trim();
      return {
        outcome: "completion",
        completion: input.completion,
        ...(reason ? { reason } : {}),
      };
    }
    case "decline": {
      const reason = input.declineReason.trim();
      const carePath = input.carePath.trim();
      return {
        outcome: "decline",
        ...(reason ? { reason } : {}),
        ...(carePath ? { carePath } : {}),
      };
    }
  }
}

/**
 * TES-1413 — what "Kopiera" was pressed on: the note the decision is written
 * into and the decision's fields, as one comparable key. Null while there is
 * nothing to copy.
 */
export function decisionNoteCopyKey(
  target: TreatmentDecisionNoteTarget | null,
  draft: TreatmentDecisionNoteDraft | null,
): string | null {
  return target && draft ? JSON.stringify({ target, draft }) : null;
}

/**
 * Whether the journal tick may stand: the note was copied and the decision
 * and its note are still the ones copied. When it turns false after a tick,
 * the tick is taken back at once.
 *
 * TES-1413 — compared on that input, never on text this bundle compiles
 * itself. The text copied is the server's; an admin tab built from another
 * version of the note layout (open across a deploy) compiles different text
 * for the same decision, so a text comparison could never come true there,
 * however often "Kopiera" was pressed. The server still compares the stored
 * text with the published decision before it accepts the tick.
 */
export function decisionNoteCopyCurrent(
  copiedKey: string | null,
  liveKey: string | null,
): boolean {
  return copiedKey !== null && liveKey !== null && copiedKey === liveKey;
}

/**
 * TES-1413 — why "Kopiera" has to wait. A positive decision's note states
 * the prescription status and, after kompletteringar, whether the plan
 * stands; both are chosen below the copy button. Copied before them, the
 * note changed the moment they were set, the tick was taken back and the
 * note had to be copied again. Null when the note is complete.
 */
export function decisionNoteCopyBlocker(input: {
  outcome: TreatmentDecisionOutcomeChoice | null;
  prescriptionChosen: boolean;
  planChoiceNeeded: boolean;
  planChoice: "kept" | "changed" | null;
  planReason: string;
}): string | null {
  if (input.outcome !== "approve") return null;
  if (input.planChoiceNeeded && input.planChoice === null)
    return "Välj först om planen kvarstår. Valet står i journalanteckningen.";
  if (input.planChoice === "changed" && !input.planReason.trim())
    return "Ange först motiveringen till ändringen. Den står i journalanteckningen.";
  if (!input.prescriptionChosen)
    return "Bekräfta först nedan att e-receptet är utfärdat i Webdoc. Receptstatusen står i journalanteckningen.";
  return null;
}
